/**
 * ChatMessageList — scrollable message list with auto-scroll on new content.
 */
import React, { useEffect, useRef } from 'react';
import { ChatMessageItem } from './ChatMessageItem';
import type { ChatMessage } from '@/services/chatApi';

// Typing indicator: three bouncing dots shown while waiting for the first chunk
const typingDotStyle: React.CSSProperties = {
  width: 7,
  height: 7,
  borderRadius: '50%',
  background: '#9fb0d8',
  display: 'inline-block',
};

const typingKeyframes = `
@keyframes chatTypingBounce {
  0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
  40%           { transform: translateY(-6px); opacity: 1; }
}
`;

function TypingIndicator() {
  return (
    <>
      <style>{typingKeyframes}</style>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-start',
          marginBottom: 8,
          padding: '0 16px',
        }}
      >
        <div
          style={{
            padding: '10px 14px',
            borderRadius: '16px 16px 16px 4px',
            background: '#f5f7fb',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
          }}
        >
          {[0, 1, 2].map(i => (
            <span
              key={i}
              style={{
                ...typingDotStyle,
                animation: `chatTypingBounce 1.2s ease-in-out ${i * 0.2}s infinite`,
              }}
            />
          ))}
        </div>
      </div>
    </>
  );
}

interface Props {
  messages: ChatMessage[];
  streamingContent: string;
  isStreaming: boolean;
}

export function ChatMessageList({ messages, streamingContent, isStreaming }: Props) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, streamingContent]);

  if (messages.length === 0 && !isStreaming) {
    return null;
  }

  // Fake streaming message to show typewriter effect
  const streamingMsg: ChatMessage | null = isStreaming && streamingContent
    ? {
        id: -1,
        session_id: -1,
        role: 'assistant',
        content: streamingContent,
        page_context: null,
        tokens_used: null,
        created_at: new Date().toISOString(),
      }
    : null;

  // Show typing indicator while waiting for the first chunk
  const showTyping = isStreaming && !streamingContent;

  return (
    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: '12px 0',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {messages.map((msg) => (
        <ChatMessageItem key={msg.id} message={msg} />
      ))}
      {showTyping && <TypingIndicator />}
      {streamingMsg && (
        <ChatMessageItem message={streamingMsg} isStreaming />
      )}
      <div ref={bottomRef} />
    </div>
  );
}
